/**
 * The THEME token set — the single source of palette, spacing, type, radii, and
 * timing the whole UI kit + scenes read from. The look is **classic Pokémon
 * Gold/Silver (GBC)**: a clean cream "paper" world with the iconic dark box
 * frame, dark slate ink, the G/S blue selection, and the green→yellow→red HP
 * bar. Calm by construction (CLAUDE.md pillar 1): bright + readable, generous
 * spacing, gentle timings, big touch targets — a faithful G/S nod kept clean,
 * never janky low-bit. Token NAMES are stable so every scene picks up the look
 * automatically; only the VALUES moved to the G/S palette.
 *
 * Pure data + tiny helpers — no Phaser import — so it is safe to use in unit
 * tests and in any layer.
 */

/**
 * Classic Gold/Silver paper palette: a bright cream world, dark slate ink, the
 * iconic dark box frame, and the G/S menu blue. Numbers are 0xRRGGBB.
 */
export const COLORS = {
  /** App backdrop — the warm G/S "paper" cream the whole world sits on. */
  bg: 0xf3f0e0,
  /** A raised surface (panels, cards) — clean near-white, a touch brighter than bg. */
  surface: 0xfbfaf2,
  /** A slightly recessed surface for nested content / inputs (soft cream). */
  surfaceAlt: 0xe7e3cf,
  /** Hairline separators / panel borders — the classic dark G/S box frame. */
  border: 0x303030,
  /** Primary text — dark slate ink on the cream paper (legible, never pure black). */
  text: 0x282420,
  /** Secondary / supporting text — a softer slate. */
  textMuted: 0x6b6358,
  /** Faint text (timestamps, hints) — light enough to recede on cream. */
  textFaint: 0x9a9384,
  /** Primary accent — the iconic G/S menu/selection BLUE (the "go" color). */
  accent: 0x4878c0,
  /** Accent pressed/active — a deeper G/S blue. */
  accentDeep: 0x2f5aa0,
  /** Warm secondary accent — the Gold-version amber, for letters/highlights. */
  warm: 0xe0a83c,
  /** A soft rose for gentle emphasis (never alarm-red). */
  rose: 0xd8707a,
  /** Scrim behind modals (used with alpha) — a dark wash so cream boxes pop. */
  scrim: 0x20201c,

  // ── Classic-Pokémon UI CHROME (the Gold/Silver message-box + menu look) ──────────
  // The iconic G/S box: a cream/white fill with a crisp dark frame and dark text,
  // plus the classic green→yellow→red HP bar and the blue selection band. These are
  // now the SAME family as the surfaces above (the whole world is the cream paper),
  // tuned to the bright, readable G/S "box" chrome.
  /** Message/menu box fill — the classic warm G/S "paper" cream. */
  boxFill: 0xf8f4e4,
  /** A subtle inner band for the box's double-border (a touch brighter cream). */
  boxFillInner: 0xfffdf6,
  /** The classic dark box border (near-black, crisp — the G/S frame). */
  boxBorder: 0x303030,
  /** A soft inner highlight line for the double-border effect. */
  boxBorderInner: 0xbab19a,
  /** Dark text inside the cream box (legible, never pure black on cream). */
  boxText: 0x282420,
  /** Secondary/dim text inside the box (labels, PP, hints). */
  boxTextMuted: 0x6b6358,
  /** Triangular selection cursor + blinking continue arrow (the dark G/S frame ink). */
  boxCursor: 0x303030,
  /** The G/S selection band behind the currently-highlighted menu row (soft blue). */
  boxHighlight: 0xbcd2f0,

  // ── The classic HP bar (green → yellow → red by HP ratio) ────────────────────────
  /** Healthy HP (> ~50%). The iconic G/S green. */
  hpGreen: 0x58b858,
  /** Caution HP (~20–50%). The iconic yellow/amber. */
  hpYellow: 0xf0c020,
  /** Critical HP (< ~20%). The iconic red. */
  hpRed: 0xe04040,
  /** The recessed track the HP fill sits in (dark, inside the box). */
  hpTrack: 0x585040,
  /** Thin frame around the HP gauge. */
  hpFrame: 0x303030,
} as const;

/** Hex string forms for Phaser Text `color` props (which want CSS strings). */
export const CSS = {
  text: '#282420',
  textMuted: '#6b6358',
  textFaint: '#9a9384',
  accent: '#4878c0',
  accentDeep: '#2f5aa0',
  warm: '#e0a83c',
  bg: '#f3f0e0',
  surface: '#fbfaf2',
  // Classic cream-box chrome text colors (CSS string forms for Phaser Text).
  boxText: '#282420',
  boxTextMuted: '#6b6358',
} as const;

/** An 8px soft spacing scale. Generous whitespace is a hard UX rule. */
export const SPACING = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

/** Corner radii — rounded, friendly. */
export const RADIUS = {
  sm: 6,
  md: 12,
  lg: 20,
  pill: 999,
} as const;

/**
 * Minimum touch target (CSS px before DPR). 44 is the platform a11y floor; we use
 * a comfortable 48 so a tired thumb never misses (cross-platform/touch HARD rule).
 */
export const TOUCH_TARGET = 48;

/**
 * Extra invisible slop added AROUND a control's hit area on a coarse-pointer (touch)
 * device, so a fat thumb that lands a few px outside the drawn edge still registers.
 * The visual is unchanged — only the interactive rect grows by this much per side.
 * Zero on a precise (mouse) pointer, where the hit area matches the visual exactly.
 */
export const TOUCH_SLOP = 6;

/**
 * True on a coarse-pointer (touch-first) device — a phone/tablet. Guarded for
 * non-browser environments (tests/SSR) where `matchMedia` is absent. Used to add a
 * little forgiving touch-slop to tap targets without affecting the mouse desktop.
 */
export function isCoarsePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

/** Type scale (px). The system font stack keeps text crisp without web-font loads. */
export const FONT = {
  family: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  /** Monospace for timestamps / numbers where alignment helps. */
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
  size: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
    xl: 28,
    title: 44,
  },
} as const;

/** Gentle animation timings (ms). Unhurried; respects "when in doubt, calm". */
export const TIMING = {
  /** Fade between scenes. */
  fade: 420,
  /** Quick UI feedback (button press). */
  tap: 120,
  /** Toast/letter on-screen dwell. */
  toast: 4200,
  /** Typewriter ms-per-character (readable, never frantic). */
  typewriter: 24,
} as const;

/** The whole theme bundled, for ergonomic single-import access. */
export const THEME = {
  colors: COLORS,
  css: CSS,
  spacing: SPACING,
  radius: RADIUS,
  touchTarget: TOUCH_TARGET,
  font: FONT,
  timing: TIMING,
} as const;

export type Theme = typeof THEME;

/** Convert a 0xRRGGBB number to a `#rrggbb` CSS string (for Phaser Text styles). */
export function hexToCss(hex: number): string {
  return `#${(hex & 0xffffff).toString(16).padStart(6, '0')}`;
}

/**
 * Resolve an effective font size given the player's UI scale setting (Settings.uiScale,
 * default 1). Clamped so text never becomes unreadably tiny or absurdly huge.
 */
export function scaledFont(sizePx: number, uiScale = 1): number {
  const clamped = Math.max(0.75, Math.min(2, uiScale));
  return Math.round(sizePx * clamped);
}

/** The three classic HP bands, by ratio (matches the GB/GBC color cutoffs). */
export type HpBand = 'green' | 'yellow' | 'red';

/** HP ratio cutoffs for the classic color change (> 0.5 green, > 0.2 yellow, else red). */
export const HP_BANDS = { yellow: 0.5, red: 0.2 } as const;

/**
 * Classify an HP ratio (0..1) into the classic green / yellow / red band. Pure +
 * tiny so the HP color logic is unit-testable and shared by HPBar + any scene that
 * still draws its own gauge (e.g. BattleScene's animated card).
 */
export function hpBand(ratio: number): HpBand {
  const r = Math.max(0, Math.min(1, ratio));
  if (r > HP_BANDS.yellow) return 'green';
  if (r > HP_BANDS.red) return 'yellow';
  return 'red';
}

/** The 0xRRGGBB fill color for an HP ratio — the iconic green→yellow→red. */
export function hpColor(ratio: number): number {
  const band = hpBand(ratio);
  return band === 'green' ? COLORS.hpGreen : band === 'yellow' ? COLORS.hpYellow : COLORS.hpRed;
}
